import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faCode,
  faEye,
  faBrush,
  faSort,
  faSortUp,
  faSortDown,
  faDownload,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import { PermissionWrapper } from "../../helpers/permission.js";

const TemplateBuilderList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const token = localStorage.getItem("authToken");

  // Filter & Search states
  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Sorting
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  // Preview Modal
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Code Export Modal
  const [exportData, setExportData] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const navigate = useNavigate();

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/template/builder/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data && response.data.data) {
        setTemplates(response.data.data);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      console.error("Error fetching visual templates:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const sortBox = (field) => {
    if (sortField === field) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  // Categories list extracted from data
  const categoryOptions = useMemo(() => {
    const defaultCats = ["Landing Page", "Email Template", "Promotion", "Newsletter", "General"];
    const fromData = templates.map((t) => t.category).filter(Boolean);
    return Array.from(new Set([...defaultCats, ...fromData]));
  }, [templates]);

  // Filtered and Sorted templates
  const filteredTemplates = useMemo(() => {
    let result = templates.filter((template) => {
      if (
        nameFilter &&
        !template.template_name
          ?.toLowerCase()
          .includes(nameFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        categoryFilter &&
        template.category !== categoryFilter
      ) {
        return false;
      }
      return true;
    });

    if (sortField) {
      result = [...result].sort((a, b) => {
        let valA = a[sortField] ?? "";
        let valB = b[sortField] ?? "";

        if (typeof valA === "number" && typeof valB === "number") {
          return sortOrder === "asc" ? valA - valB : valB - valA;
        }

        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();

        if (strA < strB) return sortOrder === "asc" ? -1 : 1;
        if (strA > strB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [templates, nameFilter, categoryFilter, sortField, sortOrder]);

  useEffect(() => {
    if (!templates.length) return;
    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [nameFilter, categoryFilter, templates]);

  // Update pagination
  useEffect(() => {
    setTotal(filteredTemplates.length);
    setPageCount(Math.ceil(filteredTemplates.length / limit) || 1);
    setPage(1);
  }, [filteredTemplates, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedTemplates = filteredTemplates.slice((page - 1) * limit, end);

  // Delete Template
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this template design!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}settings/template/builder/delete?id=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setTemplates((prev) => prev.filter((t) => t.id !== id));
        Swal.fire("Deleted!", "Your template has been deleted.", "success");
      } catch (err) {
        Swal.fire(
          "Error",
          err.response?.data?.message || "Failed to delete template.",
          "error"
        );
      }
    }
  };

  // Open Preview Modal
  const handleOpenPreview = async (id) => {
    try {
      setPreviewLoading(true);
      setIsPreviewModalOpen(true);
      const response = await axios.get(`${API_URL}settings/template/builder/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreviewTemplate(response.data.data);
    } catch (err) {
      Swal.fire("Error", "Could not load preview", "error");
      setIsPreviewModalOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Open Export Modal
  const handleOpenExport = async (id) => {
    try {
      const response = await axios.get(`${API_URL}settings/template/builder/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExportData(response.data.data);
      setIsExportModalOpen(true);
    } catch (err) {
      Swal.fire("Error", "Could not load export data", "error");
    }
  };

  // Copy Code
  const handleCopyCode = () => {
    if (!exportData) return;
    navigator.clipboard.writeText(exportData.html_content || "");
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  };

  // Download Export File
  const handleDownloadCode = () => {
    if (!exportData) return;
    const blob = new Blob([exportData.html_content || ""], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(exportData.template_name || "template")
      .toLowerCase()
      .replace(/\s+/g, "_")}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        <div className="row pb-2 align-items-center">
          <div className="col-md-6">
            <h4 className="panel-title pull-left" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FontAwesomeIcon icon={faBrush} style={{ color: "#6366f1" }} />
              <span>Streetview Builder</span>
            </h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="settings_templatebuilder" action="add">
              <Link
                to={"/Settings/TemplateBuilder/add"}
                className="btn btn-primary btn-sm"
                style={{ fontWeight: "600" }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" /> Create New Design
              </Link>
            </PermissionWrapper>

            {filteredTemplates.length > 0 && (
              <div className="d-inline-block ms-3">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          {loading || templates.length > 0 ? (
            <>
              <div className="table-responsive" style={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
                <table className="table m-b-0 table-hover">
                  <thead style={{ position: "sticky", top: 0, zIndex: 10, background: "#f8f9fa" }}>
                    <tr>
                      <th style={{ textAlign: "center", width: "60px" }}>#</th>
                      <th
                        style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                        onClick={() => sortBox("template_name")}
                      >
                        Template Name <FontAwesomeIcon icon={getSortIcon("template_name")} />
                      </th>
                      <th
                        style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                        onClick={() => sortBox("category")}
                      >
                        Category <FontAwesomeIcon icon={getSortIcon("category")} />
                      </th>
                      <th style={{ whiteSpace: "nowrap" }}>Status</th>
                      <th
                        style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                        onClick={() => sortBox("created_on")}
                      >
                        Date <FontAwesomeIcon icon={getSortIcon("created_on")} />
                      </th>
                      <th style={{ textAlign: "center", width: "160px" }}>Action</th>
                    </tr>
                    <tr style={{ background: "#ffffff" }}>
                      <th style={{ textAlign: "center" }}></th>
                      <th>
                        <div className="input-group input-group-sm">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search template name..."
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                          />
                        </div>
                      </th>
                      <th>
                        <select
                          className="form-control form-control-sm"
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                          <option value="">All Categories</option>
                          {categoryOptions.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </th>
                      <th></th>
                      <th></th>
                      <th style={{ textAlign: "center" }}>
                        <button
                          className="btn btn-default btn-sm"
                          onClick={() => {
                            setNameFilter("");
                            setCategoryFilter("");
                          }}
                          title="Clear filters"
                        >
                          Clear
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading || filterLoading ? (
                      Array.from({ length: 5 }).map((_, rowIndex) => (
                        <tr key={rowIndex}>
                          {Array.from({ length: 6 }).map((_, colIndex) => (
                            <td key={colIndex}>
                              <div
                                style={{
                                  height: "20px",
                                  background: "#f1f5f9",
                                  borderRadius: "4px",
                                }}
                              ></div>
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : paginatedTemplates.length > 0 ? (
                      paginatedTemplates.map((post, index) => (
                        <tr key={post.id}>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                            {start + index}
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <div style={{ fontWeight: "600", color: "#1e293b" }}>
                              {post.template_name}
                            </div>
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <span
                              className="badge"
                              style={{
                                background: "#e0e7ff",
                                color: "#4338ca",
                                padding: "5px 10px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              {post.category || "General"}
                            </span>
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <span
                              className={`badge ${
                                post.status === 1 ? "bg-success" : "bg-secondary"
                              }`}
                              style={{ padding: "5px 10px", borderRadius: "12px" }}
                            >
                              {post.status === 1 ? "Active" : "Draft"}
                            </span>
                          </td>
                          <td style={{ verticalAlign: "middle", fontSize: "12px", color: "#64748b" }}>
                            {post.created_on
                              ? new Date(post.created_on).toLocaleDateString("en-IN", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })
                              : "-"}
                          </td>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                            <div className="d-flex gap-1 justify-content-center">
                              <PermissionWrapper module="settings_templatebuilder" action="edit">
                                <button
                                  onClick={() =>
                                    navigate(`/Settings/TemplateBuilder/edit/${post.id}`)
                                  }
                                  className="btn btn-warning btn-xs"
                                  title="Open in Visual Designer"
                                >
                                  <FontAwesomeIcon icon={faPencil} />
                                </button>
                              </PermissionWrapper>
                              <button
                                onClick={() => handleOpenPreview(post.id)}
                                className="btn btn-info btn-xs"
                                title="Live Preview"
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button
                                onClick={() => handleOpenExport(post.id)}
                                className="btn btn-primary btn-xs"
                                title="Export HTML"
                              >
                                <FontAwesomeIcon icon={faCode} />
                              </button>
                              <PermissionWrapper module="settings_templatebuilder" action="delete">
                                <button
                                  onClick={() => handleDelete(post.id)}
                                  className="btn btn-danger btn-xs"
                                  title="Delete Template"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </PermissionWrapper>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          No visual templates found. Click "Create New Design" to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="alert alert-warning m-3">
              No visual templates found.{" "}
              <PermissionWrapper module="settings_templatebuilder" action="add">
                <Link to="/Settings/TemplateBuilder/add" className="alert-link">
                  Create your first design now
                </Link>
                .
              </PermissionWrapper>
            </div>
          )}
        </div>
      </div>

      {/* Live Preview Modal */}
      {isPreviewModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: "90vw" }}>
            <div className="modal-content" style={{ borderRadius: "12px", overflow: "hidden" }}>
              <div className="modal-header d-flex justify-content-between align-items-center py-3 bg-light">
                <h5 className="modal-title m-0 font-weight-bold">
                  <FontAwesomeIcon icon={faEye} className="me-2 text-primary" />
                  Preview: {previewTemplate?.template_name || "Template Preview"}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setIsPreviewModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    lineHeight: 1,
                    color: "#6c757d",
                    cursor: "pointer",
                    opacity: 0.8,
                    padding: "0 8px",
                  }}
                  title="Close"
                >
                  &times;
                </button>
              </div>
              <div className="modal-body p-0" style={{ height: "75vh", background: "#f8fafc" }}>
                {previewLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : (
                  <iframe
                    title="Template Preview"
                    srcDoc={previewTemplate?.html_content || ""}
                    style={{ width: "100%", height: "100%", border: "none" }}
                  />
                )}
              </div>
              <div className="modal-footer py-2 bg-light d-flex justify-content-between">
                <span className="text-muted small">
                  Category: <strong>{previewTemplate?.category || "General"}</strong>
                </span>
                <div>
                  <button
                    className="btn btn-secondary btn-sm me-2"
                    onClick={() => setIsPreviewModalOpen(false)}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setIsPreviewModalOpen(false);
                      if (previewTemplate?.id) {
                        navigate(`/Settings/TemplateBuilder/edit/${previewTemplate.id}`);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faPencil} className="me-1" /> Edit in Designer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Code Export Modal */}
      {isExportModalOpen && exportData && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "12px", overflow: "hidden" }}>
              <div className="modal-header d-flex justify-content-between align-items-center py-3 bg-light">
                <h5 className="modal-title m-0 font-weight-bold">
                  <FontAwesomeIcon icon={faCode} className="me-2 text-primary" />
                  Export HTML: {exportData.template_name}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setIsExportModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    lineHeight: 1,
                    color: "#6c757d",
                    cursor: "pointer",
                    opacity: 0.8,
                    padding: "0 8px",
                  }}
                  title="Close"
                >
                  &times;
                </button>
              </div>
              <div className="modal-body p-3">
                <p className="text-muted small mb-2">
                  Complete HTML code with embedded styles ready to be used anywhere on the website:
                </p>
                <textarea
                  readOnly
                  rows={16}
                  value={exportData.html_content || ""}
                  style={{
                    width: "100%",
                    fontFamily: "monospace",
                    fontSize: "13px",
                    background: "#0f172a",
                    color: "#e2e8f0",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #334155",
                    resize: "vertical",
                  }}
                />
              </div>
              <div className="modal-footer py-2 bg-light d-flex justify-content-between">
                <div>
                  {copySuccess && (
                    <span className="text-success small me-2 font-weight-bold">
                      ✓ Copied to clipboard!
                    </span>
                  )}
                </div>
                <div>
                  <button
                    className="btn btn-secondary btn-sm me-2"
                    onClick={() => setIsExportModalOpen(false)}
                  >
                    Close
                  </button>
                  <button className="btn btn-outline-primary btn-sm me-2" onClick={handleCopyCode}>
                    <FontAwesomeIcon icon={faCopy} className="me-1" /> Copy HTML
                  </button>
                  <button className="btn btn-success btn-sm" onClick={handleDownloadCode}>
                    <FontAwesomeIcon icon={faDownload} className="me-1" /> Download .html
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateBuilderList;
